import { useTranslation } from 'react-i18next';
import { useAssistanceContext } from './AssistanceContext.tsx';
import DOMPurify from 'dompurify';
import Button from '../../../core/components/Button.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import Explanation, { localizeExplanationDetails } from './Explanation.tsx';
import { getConfidenceExplanationIcon } from './ConfidenceExplanation.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faDatabase, faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';

const ErrorIcon = withIcon(faExclamationCircle);
const DatabaseIcon = withIcon(faDatabase);

enum MatchingResult {
    no_document_found = 'no_document_found',
    no_document_item_found = 'no_document_item_found',
    values_not_match = 'values_not_match',
    values_match = 'values_match',
    value_in_range = 'value_in_range',
    deviation_accepted = 'deviation_accepted',
    ignore_matching = 'ignore_matching',
}

const matchingTextMessagesAndStyles = {
    no_document_found: ['error', 'OC-01'], // case 1
    no_document_item_found: ['error', 'OC-02'], // case 2
    field_matching_required_or_optional_matching_ignored: ['neutral', 'OC-12'], // case 23
    field_mandatory__matching_required__field_empty: ['error', 'OC-10'], // case 12
    field_mandatory__matching_optional__field_empty: ['neutral', 'OC-10'], // case 13
    field_mandatory__matching_required__threshold__field_not_empty__values_match: ['success', 'OC-03'], // case 6
    field_mandatory__matching_required__threshold__field_not_empty__value_in_range: ['warning', 'OC-06'], // case 7
    field_mandatory__matching_required__threshold__field_not_empty__values_not_match: ['error', 'OC-11'], // case 8
    field_mandatory__matching_required__threshold__field_not_empty__deviation_accepted: ['success', 'OC-05'], // case 9
    field_mandatory__matching_required__field_not_empty__values_match: ['success', 'OC-03'], // case 3
    field_mandatory__matching_required__field_not_empty__values_not_match: ['error', 'OC-04'], // case 4
    field_mandatory__matching_required__field_not_empty__deviation_accepted: ['success', 'OC-05'], // case 5
    field_mandatory__matching_optional__field_not_empty__values_match: ['neutral', 'OC-07'], // case 10
    field_mandatory__matching_optional__field_not_empty__values_not_match: ['warning', 'OC-09'], // case 11
    field_optional__matching_required__threshold__field_not_empty__values_match: ['success', 'OC-03'], // case 17
    field_optional__matching_required__threshold__field_not_empty__value_in_range: ['warning', 'OC-06'], // case 18
    field_optional__matching_required__threshold__field_not_empty__values_not_match: ['error', 'OC-11'], // case 19
    field_optional__matching_required__threshold__field_not_empty__deviation_accepted: ['success', 'OC-05'], // case 20
    field_optional__matching_required__field_not_empty__values_match: ['success', 'OC-03'], // case 14
    field_optional__matching_required__field_not_empty__values_not_match: ['error', 'OC-04'], // case 15
    field_optional__matching_required__field_not_empty__deviation_accepted: ['success', 'OC-05'], // case 16
    field_optional__matching_optional__field_not_empty__values_match: ['neutral', 'OC-07'], // case 21
    field_optional__matching_optional__field_not_empty__values_not_match: ['neutral', 'OC-09'], // case 22
    // added cases
    // field_optional__matching_required__field_empty: ['error', 'OC-10'], // case 12
    // field_optional__matching_optional__field_empty: ['neutral', 'OC-10'], // case 13
};

const matchingAcceptDeviationVisible = [
    'field_mandatory__matching_required__field_not_empty__values_not_match', // case 4
    'field_mandatory__matching_required__threshold__field_not_empty__value_in_range', // case 7
    'field_mandatory__matching_required__threshold__field_not_empty__values_not_match', // case 8
    'field_optional__matching_required__field_not_empty__values_not_match', // case 15
    'field_optional__matching_required__threshold__field_not_empty__values_not_match', // case 19
    'field_matching_required_or_optional_matching_ignored', // case 23
];

const matchingActions = {
    field_mandatory__matching_required__field_not_empty__values_not_match: 'document_matching_accept_deviation', // case 4
    field_mandatory__matching_required__threshold__field_not_empty__value_in_range:
        'document_matching_accept_deviation', // case 7
    field_mandatory__matching_required__threshold__field_not_empty__values_not_match:
        'document_matching_accept_deviation', // case 8
    field_optional__matching_required__field_not_empty__values_not_match: 'document_matching_accept_deviation', // case 15
    field_optional__matching_required__threshold__field_not_empty__values_not_match:
        'document_matching_accept_deviation', // case 19
    field_matching_required_or_optional_matching_ignored: 'document_matching_restore_matching', // case 23
};

export const getMatchingKey = (explanationDetails: any, isFieldRequired = false, isFieldEmpty = false) => {
    const matchingResult: MatchingResult = explanationDetails?.document_matching_result;

    const isMatchingRequired: boolean = explanationDetails?.block_automation;
    const hasThreshold = explanationDetails?.comparison_type !== 'exact_match';

    let key = '';

    if (matchingResult === 'no_document_found') {
        key = 'no_document_found';
    } else if (matchingResult === 'no_document_item_found') {
        key = 'no_document_item_found';
    } else if (matchingResult === 'ignore_matching') {
        key = 'field_matching_required_or_optional_matching_ignored'; // case 23
    } else {
        // we don't need to check for not supported as only the list above is supported
        key += isFieldRequired ? 'field_mandatory' : 'field_optional';
        key += isMatchingRequired ? '__matching_required' : '__matching_optional';
        key += hasThreshold ? '__threshold' : '';
        key += isFieldEmpty ? '__field_empty' : '__field_not_empty';
        key += !isFieldEmpty ? '__' + matchingResult : '';
    }

    return key;
};

const getMatchingTextMessagesAndStyles = (key: string) => {
    // previously "not_supported" -> instead of red error we fall back to extraction logic and show neutral
    return matchingTextMessagesAndStyles[key] || ['neutral', ''];
};

export const getMatchingSeverity = (key: string) => {
    return getMatchingTextMessagesAndStyles(key)[0];
};

export const useMatchingExplanationProps = (
    confidenceExplanation: any,
    dateFormat: string,
    isFieldRequired = false,
    isFieldEmpty = false
) => {
    const { t, i18n } = useTranslation('assistance');

    const explanationDetails: any = confidenceExplanation?.explanationDetails || {};
    explanationDetails['document_matching_name'] = explanationDetails['document_matching_name'] || 'order';
    explanationDetails['threshold_unit'] = t(`assistance:explanation.${explanationDetails?.comparison_type}`);

    const matchingResult: MatchingResult = explanationDetails?.document_matching_result;
    if (matchingResult == null) {
        return { message: '', severity: '', acceptDeviationVisible: false, Icon: undefined };
    }

    const key = getMatchingKey(explanationDetails, isFieldRequired, isFieldEmpty);
    const [severity, translationKey] = getMatchingTextMessagesAndStyles(key);
    if (!translationKey) {
        // no document matching explanation available
        console.warn('No document matching explanation available', key);
        return { message: '', severity: '', acceptDeviationVisible: false, Icon: undefined };
    }

    const isAcceptable = matchingAcceptDeviationVisible.includes(key);
    const acceptActionKey = matchingActions[key];

    const localizedExplanationDetails = localizeExplanationDetails(t, explanationDetails, dateFormat, i18n);
    const message: string = translationKey
        ? t(`explanation.${translationKey}`, localizedExplanationDetails).toString()
        : '';
    const Icon = severity === 'error' ? ErrorIcon : DatabaseIcon;

    return { message, severity, isAcceptable, acceptActionKey, Icon };
};

const MatchingExplanation = ({
    message,
    severity,
    isAcceptable,
    acceptActionKey,
    onAccept,
}: {
    message: string;
    severity: string;
    isAcceptable?: boolean;
    acceptActionKey?: string;
    onAccept?: () => void;
}) => {
    const { t } = useTranslation('assistance');
    const { readOnly } = useAssistanceContext();

    return (
        <Explanation severity={severity}>
            {message && <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message) }} />}
            {isAcceptable ? (
                <Button
                    onClick={(e) => onAccept()}
                    className={classnames(
                        'text-xs mt-2',
                        severity === 'error' && 'border-line-danger-strong ',
                        severity === 'warning' && 'border-line-warning-strong',
                        severity === 'success' && 'border-line-success-strong'
                    )}
                    disabled={readOnly}
                >
                    {t(`assistance:${acceptActionKey}`)}
                </Button>
            ) : null}
        </Explanation>
    );
};

export default MatchingExplanation;
